import { useContext, useState } from 'react';
import { useQuery } from 'react-query';
import { SettingsContext } from '../store/SettingsContext';
import { UserContext } from '../store/UserContext';
import useAxios from './useAxios';
import { LovenseSettingItem } from './useLovense';
import useSubscriber from './useSubscriber';

const useLovenseSettings = () => {
    const userContext = useContext(UserContext);
    const settingsContext = useContext(SettingsContext);
    const { data: subscriber } = useSubscriber();
    const axios = useAxios();

    const [isUpdatingSettings, setIsUpdatingSettings] = useState<boolean>(false);

    // Fetch Subscriber's Mass DM
    const fetchData = async (): Promise<LovenseSettingItem[] | null> => {
        const query: string = `${settingsContext.routes.lovense.base}${subscriber._id}/settings`;

        return axios
            .get(query)
            .then(response => response.data)
            .catch(() => {
                return null;
            });
    };

    const lovenseToysSettings = useQuery(['devicesLovenseSettings', subscriber?._id], () => fetchData(), {
        refetchOnWindowFocus: false,
        staleTime: 5 * 60 * 1000, // 5 minutes
        enabled: userContext.jwtToken && settingsContext.apiKey && subscriber && subscriber._id ? true : false,
    });

    // Save/update settings
    const saveLovenseSettings = async (newSettings: LovenseSettingItem) => {
        setIsUpdatingSettings(true);

        const query: string = `${settingsContext.routes.lovense.base}${subscriber._id}/settings`;

        return axios
            .post(query, newSettings)
            .then(response => {
                if (response.status !== 200) {
                    throw new Error('Failed to save Lovense settings.');
                }

                // Refetch settings
                lovenseToysSettings.refetch();

                return response.data;
            })
            .catch(error => {
                throw new Error(error.message || 'Failed to save Lovense settings.');
            })
            .finally(() => {
                setIsUpdatingSettings(false);
            });
    };

    // Delete settings
    const deleteLovenseSettings = async (settingId: string): Promise<{ success: boolean }> => {
        setIsUpdatingSettings(true);

        const query: string = `${settingsContext.routes.lovense.base}${subscriber._id}/settings/${settingId}`;

        return axios
            .delete(query)
            .then(response => {
                if (response.status !== 200) {
                    throw new Error('Failed to delete Lovense settings.');
                }

                // Refetch settings
                lovenseToysSettings.refetch();

                return response.data;
            })
            .catch(error => {
                throw new Error(error.message || 'Failed to delete Lovense settings.');
            })
            .finally(() => {
                setIsUpdatingSettings(false);
            });
    };

    return {
        ...lovenseToysSettings,
        isUpdatingSettings,
        saveLovenseSettings,
        deleteLovenseSettings,
    };
};

export default useLovenseSettings;
